import "../globals.css";

import {
	HeadContent,
	Outlet,
	Scripts,
	createRootRoute,
} from "@tanstack/react-router";
import { queryClient } from "@/lib/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "@/components/ui/sonner";
import { seo } from "@/lib/seo";
import { DefaultCatchBoundary } from "@/modules/root/pages/DefaultCatchBoundary";
import { NotFound } from "@/modules/root/pages/NotFound";
import { I18nextProvider } from "react-i18next";
import i18n from "@/services/i18n/i18n";
import { composeProviders, createProvider } from "@/lib/composeProviders";
import { lazy, ReactNode, Suspense } from "react";
import { checkSessionFn } from "@/modules/auth/lib/authenticationFns";
import {
	clearSession,
	setSession,
} from "@/modules/auth/lib/authenticationClient";
import { authSessionSchema } from "@/modules/auth/schemes";

export const Route = createRootRoute({
	beforeLoad: async () => {
		const sessionResponse = await checkSessionFn();

		if (sessionResponse.error) {
			return;
		}

		return { user: sessionResponse?.user, token: sessionResponse?.token };
	},
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			...seo({
				title: "Tecniphone",
				description: "A unified solution for manage repairs",
			}),
		],
	}),
	errorComponent: (props) => (
		<RootDocument>
			<DefaultCatchBoundary {...props} />
		</RootDocument>
	),
	notFoundComponent: () => <NotFound />,
	component: RootComponent,
});

const isDevtoolsEnabled = import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEVTOOLS === "true";

const TanStackRouterDevtools = !isDevtoolsEnabled
	? () => null
	: lazy(() =>
			import("@tanstack/react-router-devtools").then((res) => ({
				default: res.TanStackRouterDevtools,
			})),
		);

function RootComponent() {
	const routeContext = Route.useRouteContext();
	const parsedSession = authSessionSchema.safeParse({
		token: routeContext?.token,
		user: routeContext?.user,
	});

	if (parsedSession.success) {
		setSession(parsedSession.data);
	} else {
		clearSession();
	}

	const AllInOneProvider = composeProviders([
		createProvider(QueryClientProvider, { client: queryClient }),
		createProvider(I18nextProvider, { i18n }),
	]);

	return (
		<AllInOneProvider>
			<RootDocument>
				<Outlet />
				<Toaster richColors position="bottom-center" />
			</RootDocument>
		</AllInOneProvider>
	);
}

function RootDocument({ children }: { children: ReactNode }) {
	return (
		<html
			lang="pt-PT"
			data-theme-mode="system"
			data-content-layout="full-width"
			data-navbar-style="sticky"
			data-sidebar-variant="inset"
			data-sidebar-collapsible="icon"
			data-font="inter"
			suppressHydrationWarning
		>
			<head>
				<HeadContent />
			</head>
			<body className="font-sans min-h-screen antialiased">
				{children}
				{isDevtoolsEnabled ? (
					<Suspense>
						<TanStackRouterDevtools />
						<ReactQueryDevtools />
					</Suspense>
				) : null}
				<Scripts />
			</body>
		</html>
	);
}
